import React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import BLOG_POSTS, { BLOG_AUTHOR } from "./blogData";
import "./BlogList.css";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const PAGE_SIZE = 10;

const BlogList = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const pageParam = parseInt(query.get("page") || "1", 10);
  const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;

  // Sort posts by date desc (fallback to original order if parse fails)
  const posts = React.useMemo(() => {
    const parse = (d) => Date.parse(d);
    return [...BLOG_POSTS].sort((a, b) => {
      const da = parse(a.date);
      const db = parse(b.date);
      if (Number.isNaN(da) || Number.isNaN(db)) return 0;
      return db - da;
    });
  }, []);

  const totalPages = Math.ceil(posts.length / PAGE_SIZE) || 1;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pageItems = posts.slice(start, end);

  React.useEffect(() => {
    const suffix = safePage > 1 ? ` | Blog Page ${safePage}` : " | Blog";
    document.title = `Megavolt Electric${suffix}`;
  }, [safePage]);

  const goToPage = (p) => {
    navigate(`/blog?page=${p}`);
  };

  return (
    <div className="blog-page">
      <header className="blog-header">
        <h1>Blog</h1>
      </header>

      <div className="blog-list">
        {pageItems.map((post) => {
          const hasImage = Boolean(post.image);
          return (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className={`blog-card ${hasImage ? "has-image" : ""}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {hasImage && (
                <img
                  className="blog-card-image"
                  src={post.image}
                  alt={post.title}
                />
              )}
              <div className="blog-card-content">
                <h3 className="blog-card-title">{post.title}</h3>
                <div className="blog-card-meta">
                  <span>{post.date}</span>
                  {" • "}
                  <span>By {BLOG_AUTHOR}</span>
                </div>
                <p className="blog-card-excerpt">{post.excerpt}</p>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="paginator">
        <button onClick={() => goToPage(safePage - 1)} disabled={safePage <= 1}>
          Prev
        </button>
        {Array.from({ length: totalPages }).map((_, i) => {
          const p = i + 1;
          return (
            <button
              key={p}
              className={p === safePage ? "active" : ""}
              onClick={() => goToPage(p)}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={() => goToPage(safePage + 1)}
          disabled={safePage >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BlogList;
